import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { DateTimePicker, combineDateTime } from "./DateTimePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const SEVERITY_LABEL = { P1: "P1 Critical", P2: "P2 High", P3: "P3 Medium", P4: "P4 Low" };
const EMPTY = { title: "", description: "", severity: "P3", detected_date: null, detected_time: "09:00", personal_data_breach: false, affected_orgs: "" };

export function IncidentCreateDialog({ open, onOpenChange, onCreate }) {
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const { detected_date, detected_time, ...rest } = form;
    const ok = await onCreate({
      ...rest,
      detected_at: combineDateTime(detected_date, detected_time),
      affected_orgs: form.affected_orgs.split(",").map((s) => s.trim()).filter(Boolean),
    });
    if (ok) setForm(EMPTY);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Log an incident</DialogTitle>
          <DialogDescription>Record it as soon as it is suspected. The 72-hour GDPR clock starts from the detected time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="incident-title-input" /></div>
          <div className="space-y-1"><Label>What happened</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="incident-description-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Severity</Label>
              <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
                <SelectTrigger data-testid="incident-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SEVERITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Detected</Label>
              <DateTimePicker date={form.detected_date} time={form.detected_time} onDate={(d) => set("detected_date", d || null)} onTime={(t) => set("detected_time", t)} />
            </div>
          </div>
          <div className="space-y-1"><Label>Affected organisations (comma separated)</Label><Input value={form.affected_orgs} onChange={(e) => set("affected_orgs", e.target.value)} data-testid="incident-orgs-input" /></div>
          <div className="flex items-center justify-between p-3 border rounded-sm">
            <div><p className="text-sm font-medium">Personal data breach</p><p className="text-xs text-slate-500">Starts the 72-hour regulator notification clock</p></div>
            <Switch checked={form.personal_data_breach} onCheckedChange={(v) => set("personal_data_breach", v)} data-testid="incident-breach-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={form.title.trim().length < 3} data-testid="incident-create-submit">Log incident</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
